/**
 * SocialPulseExplore — /explore hub.
 *
 * The SocialPulse-branded discovery landing. Not a data-heavy explorer —
 * it's an *invitation*: pick a scope (country / state / city / village),
 * jump into a curated set of places, or fan out to a specialist site.
 * Uses the SocialPulse palette (emerald / violet / blue / orange /
 * lavender) with no dark surfaces.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ArrowRight, Compass, Globe2, MapPin, Building2, Trees, Users,
  Landmark, Newspaper, Camera, Utensils, Plane, Loader2,
} from "lucide-react";
import { API } from "@/lib/api";
import SocialPulseHeader from "@/components/socialpulse/SocialPulseHeader";
import SocialPulseFooter from "@/components/socialpulse/SocialPulseFooter";

const SCOPES = [
  { key: "country",  label: "Countries",              lead: "Start with a country and drill into any level.", icon: Globe2,   tint: "blue",    href: "/places?level=country" },
  { key: "state",    label: "States & Provinces",     lead: "50 US states, 28 Indian states, and growing.",   icon: Landmark, tint: "violet",  href: "/places?level=state" },
  { key: "city",     label: "Cities & Towns",         lead: "Local news, civic pulse, community stories.",    icon: Building2,tint: "emerald", href: "/places?level=city" },
  { key: "village",  label: "Villages & Communities", lead: "Family-scale places with living portals.",       icon: Trees,    tint: "orange",  href: "/places?level=village" },
];

const FALLBACK_HIGHLIGHTS = [
  { name: "Kerala",       lead: "God's own country — beaches, backwaters, monsoon and Malayalam.", href: "/kerala",                  tint: "emerald", country: "India" },
  { name: "Kadanad",      lead: "A village in the high ranges — the founder's home.",              href: "/l/in/kerala/kadanad",     tint: "teal",    country: "Kerala, India" },
  { name: "New York",     lead: "Neighborhood pulses, civic reports and community stories.",       href: "/l/us/new-york",           tint: "blue",    country: "USA" },
  { name: "San Jose",     lead: "Silicon Valley culture and community.",                           href: "/l/us/california/san-jose",tint: "violet",  country: "California, USA" },
  { name: "Chennai",      lead: "South India's cultural and coastal capital.",                     href: "/l/in/tamil-nadu/chennai", tint: "orange",  country: "Tamil Nadu, India" },
  { name: "Bengaluru",    lead: "India's tech hub with a warm neighborhood pulse.",                href: "/l/in/karnataka/bengaluru",tint: "rose",    country: "Karnataka, India" },
];

// Curated slugs — hand-picked places we want front-and-centre. When the
// backend `/api/locations` returns matching docs we use their live titles
// + hero images; otherwise we fall back to the static tiles above.
const FEATURED_SLUGS = [
  { slug: "india/kerala",                    fallback: FALLBACK_HIGHLIGHTS[0] },
  { slug: "india/kerala/kottayam/kadanad",   fallback: FALLBACK_HIGHLIGHTS[1] },
  { slug: "united-states/new-york",          fallback: FALLBACK_HIGHLIGHTS[2] },
  { slug: "united-states/california/san-jose", fallback: FALLBACK_HIGHLIGHTS[3] },
  { slug: "india/tamil-nadu/chennai",        fallback: FALLBACK_HIGHLIGHTS[4] },
  { slug: "india/karnataka/bengaluru",       fallback: FALLBACK_HIGHLIGHTS[5] },
];

const TRACKS = [
  { key: "community", label: "Community",     lead: "Meet neighbors, share stories, join local groups.", icon: Users,     tint: "violet", href: "/community" },
  { key: "civic",     label: "Civic Issues",  lead: "Report and follow potholes, water, waste, safety.", icon: Landmark,  tint: "emerald", href: "/civic" },
  { key: "news",      label: "News",          lead: "Local and network-wide news across the pulse.",      icon: Newspaper, tint: "blue",    href: "/topics/news" },
  { key: "photos",    label: "Photography",   lead: "Local photo essays and community coverage.",         icon: Camera,    tint: "rose",    href: "/topics/photography" },
  { key: "food",      label: "Food & Basics", lead: "Local food, community meals, food banks.",           icon: Utensils,  tint: "orange",  href: "/topics/food" },
  { key: "travel",    label: "Travel",        lead: "Destinations, itineraries and traveller stories.",   icon: Plane,     tint: "teal",    href: "/topics/travel" },
];

export default function SocialPulseExplore() {
  const [highlights, setHighlights] = useState(FALLBACK_HIGHLIGHTS);
  const [loadingLive, setLoadingLive] = useState(true);

  useEffect(() => {
    document.title = "Explore SocialPulse — Places, Communities, Topics";
    setMeta("description", "Explore SocialPulse — start with a country, state, city or village, or dive into community, civic, news, photography, food and travel across the network.");
    setLink("canonical", "https://socialpulse.com/explore");
    setMeta("robots", "index, follow");
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const results = await Promise.all(FEATURED_SLUGS.map(async ({ slug, fallback }) => {
          try {
            const r = await axios.get(`${API}/locations/${slug}`, { timeout: 5000 });
            const d = r.data || {};
            const loc = d.city || d.state || d.country || fallback.country;
            const hero = d.hero_image || (Array.isArray(d.photos) && d.photos[0]) || null;
            const desc = d.short_description || d.summary || d.description || fallback.lead;
            return {
              name:    d.name || fallback.name,
              country: [d.state, d.country].filter(Boolean).join(", ") || loc || fallback.country,
              lead:    (typeof desc === "string" && desc.length > 6) ? _truncate(desc, 140) : fallback.lead,
              href:    `/l/${slug}`,
              tint:    fallback.tint,
              image:   hero,
            };
          } catch {
            return { ...fallback, image: null };
          }
        }));
        if (!cancelled) setHighlights(results);
      } catch {
        // Keep fallbacks silently.
      } finally {
        if (!cancelled) setLoadingLive(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="sp-explore-page">
      <SocialPulseHeader />

      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{
          background:
            "linear-gradient(140deg, #F0F6FF 0%, #F5F1FA 40%, #ECFDF5 100%)",
        }}
      >
        <div className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 pt-12 sm:pt-16 pb-8 sm:pb-12 relative">
          {/* Gradient orbs */}
          <div
            aria-hidden
            className="absolute -top-16 -left-16 w-72 h-72 rounded-full blur-3xl opacity-50 pointer-events-none"
            style={{ background: "radial-gradient(circle, #A7F3D0 0%, transparent 70%)" }}
          />
          <div
            aria-hidden
            className="absolute top-8 right-0 w-80 h-80 rounded-full blur-3xl opacity-45 pointer-events-none"
            style={{ background: "radial-gradient(circle, #DDD6FE 0%, transparent 75%)" }}
          />

          <div className="relative">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[.22em] text-violet-700">
              <Compass className="w-3.5 h-3.5" /> Explore
            </div>
            <h1
              className="mt-3 font-serif font-bold tracking-tight text-slate-900 leading-[1.05] text-3xl sm:text-4xl lg:text-5xl max-w-3xl"
              data-testid="sp-explore-h1"
            >
              Start with a <span className="text-emerald-700">place</span>.
              End with a <span className="text-violet-700">community</span>.
            </h1>
            <p className="mt-4 sm:mt-5 text-slate-600 text-base sm:text-lg leading-relaxed max-w-2xl">
              Every SocialPulse portal is built around a place — a country, a
              state, a city, a village. Pick a scope, follow a topic, or fan
              out across the whole network.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/search"
                data-testid="sp-explore-cta-search"
                className="inline-flex items-center gap-2 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold px-5 py-2.5 text-sm transition-colors shadow-sm"
              >
                Search the network <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/network"
                data-testid="sp-explore-cta-network"
                className="inline-flex items-center gap-2 rounded-full ring-1 ring-slate-300 hover:ring-violet-400 text-slate-700 hover:text-violet-700 font-semibold px-5 py-2.5 text-sm transition-colors bg-white/70 backdrop-blur"
              >
                Every sister site
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Scopes */}
      <section className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 py-10 sm:py-14">
        <h2 className="font-serif font-bold text-slate-900 text-xl sm:text-2xl">
          Explore by scope
        </h2>
        <p className="mt-1 text-slate-500 text-sm">
          World → Country → State / Province → City → Village → Community.
        </p>
        <ul
          className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
          data-testid="sp-explore-scopes"
        >
          {SCOPES.map((s) => <ScopeCard key={s.key} {...s} />)}
        </ul>
      </section>

      {/* Highlights */}
      <section className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 pb-14">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
          <div>
            <h2 className="font-serif font-bold text-slate-900 text-xl sm:text-2xl">
              Featured places
            </h2>
            <p className="mt-1 text-slate-500 text-sm flex items-center gap-1.5">
              {loadingLive ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading live portals…
                </>
              ) : (
                "A quick taste of what a SocialPulse portal can be."
              )}
            </p>
          </div>
          <Link
            to="/places"
            data-testid="sp-explore-all-places"
            className="text-sm font-semibold text-violet-700 hover:text-violet-900 inline-flex items-center gap-1"
          >
            All places <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <ul
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          data-testid="sp-explore-highlights"
        >
          {highlights.map((p) => <PlaceCard key={p.name} {...p} />)}
        </ul>
      </section>

      {/* Tracks */}
      <section className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 pb-14">
        <h2 className="font-serif font-bold text-slate-900 text-xl sm:text-2xl">
          Or dive into a topic
        </h2>
        <ul
          className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3"
          data-testid="sp-explore-tracks"
        >
          {TRACKS.map((t) => <TrackCard key={t.key} {...t} />)}
        </ul>
      </section>

      {/* CTA */}
      <section className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 pb-16">
        <div
          className="rounded-3xl ring-1 ring-violet-200 p-6 sm:p-8"
          style={{
            background:
              "linear-gradient(135deg, #ECFDF5 0%, #F5F1FA 55%, #FEF3C7 100%)",
          }}
          data-testid="sp-explore-cta"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <span className="w-11 h-11 grid place-items-center rounded-xl bg-gradient-to-br from-emerald-500 to-violet-600 text-white shrink-0 shadow-sm">
              <MapPin className="w-5 h-5" strokeWidth={2.5} />
            </span>
            <div className="flex-1 min-w-0">
              <h2 className="font-serif font-bold text-slate-900 text-xl sm:text-2xl">
                Don't see your place yet?
              </h2>
              <p className="mt-1.5 text-sm sm:text-base text-slate-700 max-w-2xl">
                Every SocialPulse portal starts somewhere. Request a place and
                help us build the first draft with local knowledge.
              </p>
            </div>
            <Link
              to="/places?action=request"
              data-testid="sp-explore-request-place"
              className="inline-flex items-center gap-2 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold px-5 py-2.5 text-sm transition-colors shrink-0 shadow-sm"
            >
              Request a place <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <SocialPulseFooter />
    </div>
  );
}

/* ─────────────────── UI atoms ─────────────────── */

const TINTS = {
  violet:   { bg: "bg-violet-50",   text: "text-violet-700",   ring: "ring-violet-200",   bar: "from-violet-200 to-violet-100" },
  emerald:  { bg: "bg-emerald-50",  text: "text-emerald-700",  ring: "ring-emerald-200",  bar: "from-emerald-200 to-emerald-100" },
  blue:     { bg: "bg-blue-50",     text: "text-blue-700",     ring: "ring-blue-200",     bar: "from-blue-200 to-blue-100" },
  orange:   { bg: "bg-orange-50",   text: "text-orange-700",   ring: "ring-orange-200",   bar: "from-orange-200 to-orange-100" },
  rose:     { bg: "bg-rose-50",     text: "text-rose-700",     ring: "ring-rose-200",     bar: "from-rose-200 to-rose-100" },
  teal:     { bg: "bg-teal-50",     text: "text-teal-700",     ring: "ring-teal-200",     bar: "from-teal-200 to-teal-100" },
};

function ScopeCard({ label, lead, icon: Icon, tint, href }) {
  const t = TINTS[tint] || TINTS.violet;
  return (
    <li>
      <Link
        to={href}
        data-testid={`sp-explore-scope-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        className="group flex flex-col gap-3 rounded-2xl ring-1 ring-slate-200 bg-white overflow-hidden hover:ring-violet-300 hover:shadow-md transition-all h-full"
      >
        <div className={`h-1.5 bg-gradient-to-r ${t.bar}`} />
        <div className="p-5 flex-1 flex flex-col gap-3">
          <span className={`w-11 h-11 grid place-items-center rounded-2xl ${t.bg} ${t.text} ring-1 ${t.ring}`}>
            <Icon className="w-5 h-5" strokeWidth={2.2} />
          </span>
          <div>
            <h3 className={`font-serif font-bold text-lg leading-tight ${t.text}`}>{label}</h3>
            <p className="mt-1 text-sm text-slate-600 leading-relaxed">{lead}</p>
          </div>
          <span className="mt-auto inline-flex items-center gap-1 text-xs font-semibold text-slate-500 group-hover:text-violet-700">
            Open <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </Link>
    </li>
  );
}

function PlaceCard({ name, lead, href, tint, country, image }) {
  const t = TINTS[tint] || TINTS.violet;
  return (
    <li>
      <Link
        to={href}
        data-testid={`sp-explore-place-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        className="group flex flex-col rounded-2xl ring-1 ring-slate-200 bg-white overflow-hidden hover:ring-violet-300 hover:shadow-md transition-all h-full"
      >
        <div className={`h-28 bg-gradient-to-br ${t.bar} relative overflow-hidden`}>
          {image && (
            <img
              src={image}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          )}
          <span className="absolute top-3 left-3 text-[10px] font-semibold uppercase tracking-[.15em] text-slate-700 bg-white/85 backdrop-blur px-2 py-0.5 rounded-full max-w-[85%] truncate">
            {country}
          </span>
        </div>
        <div className="p-5 flex-1 flex flex-col gap-2">
          <h3 className={`font-serif font-bold text-lg leading-tight ${t.text}`}>{name}</h3>
          <p className="text-sm text-slate-600 leading-relaxed">{lead}</p>
          <span className="mt-auto inline-flex items-center gap-1 text-xs font-semibold text-slate-500 group-hover:text-violet-700 pt-2">
            Visit portal <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </Link>
    </li>
  );
}

function TrackCard({ label, icon: Icon, tint, href }) {
  const t = TINTS[tint] || TINTS.violet;
  return (
    <li>
      <Link
        to={href}
        data-testid={`sp-explore-track-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className="flex flex-col items-center text-center gap-2 rounded-2xl bg-white ring-1 ring-slate-200 p-4 hover:ring-violet-300 hover:shadow-md transition-all h-full"
      >
        <span className={`w-10 h-10 grid place-items-center rounded-xl ${t.bg} ${t.text} ring-1 ${t.ring}`}>
          <Icon className="w-4 h-4" strokeWidth={2.2} />
        </span>
        <span className="text-[13px] font-semibold text-slate-800 leading-tight">
          {label}
        </span>
      </Link>
    </li>
  );
}

function _truncate(s, n) {
  if (!s || s.length <= n) return s;
  return s.slice(0, n - 1).trimEnd() + "…";
}

function setMeta(name, content, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
}
